import * as React from "react";
import { Box, Paper, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import {
  Bar, BarChart, CartesianGrid, LabelList, LabelProps, Legend, ResponsiveContainer, Tooltip, TooltipPayload, XAxis,
  YAxis,
} from "recharts";
import {
  ArmMixRow, CampaignRow, ChartMode, FunnelRow, PanelSplitRow, Series, share, SINGLE_SERIES, TimelineRow,
} from "./cohortOverview";

const ROW_HEIGHT = 36;
const LEGEND_HEIGHT = 48;
const AXIS_WIDTH = 190;
const TICK = { fontSize: 12 };

export interface ChartTheme {
  mode: ChartMode;
  ink: string;
  text: string;
  grid: string;
  surface: string;
  hover: string;
}

export const useChartTheme = (): ChartTheme => {
  const theme = useTheme();
  return {
    mode: theme.palette.type === "dark" ? "dark" : "light",
    ink: theme.palette.text.secondary,
    text: theme.palette.text.primary,
    grid: theme.palette.divider,
    surface: theme.palette.background.paper,
    hover: theme.palette.action.hover,
  };
};

const tooltipStyles = (t: ChartTheme) => ({
  contentStyle: { background: t.surface, border: `1px solid ${t.grid}`, color: t.text },
  itemStyle: { color: t.text },
  labelStyle: { color: t.text, fontWeight: 600 },
  cursor: { fill: t.hover },
});

const barsHeight = (rows: number, legend: boolean): number => rows * ROW_HEIGHT + (legend ? LEGEND_HEIGHT : 24);

export const ChartCard: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle, children }) => (
  <Paper style={{ height: "100%" }}>
    <Box p={2}>
      <Typography variant="subtitle1" style={{ fontWeight: 600 }}>{title}</Typography>
      {subtitle && <Typography variant="body2" color="textSecondary">{subtitle}</Typography>}
      <Box mt={2}>{children}</Box>
    </Box>
  </Paper>
);

export const EmptyChart: React.FC<{ text: string }> = ({ text }) => (
  <Box py={4} textAlign="center">
    <Typography variant="body2" color="textSecondary">{text}</Typography>
  </Box>
);

const countOf = (entry: TooltipPayload, name: string): number => Number(entry.payload?.counts?.[name] ?? 0);

// Plain SVG text: the default label wraps to the width of the bar it sits on.
const endLabel = (t: ChartTheme) => function EndLabel(props: LabelProps): React.ReactElement {
  const { x = 0, y = 0, width = 0, height = 0, value } = props;
  return (
    <text x={x + width + 6} y={y + height / 2} dy="0.35em" fill={t.ink} fontSize={12}>
      {value}
    </text>
  );
};

const SHARE_TICKS = [0, 0.25, 0.5, 0.75, 1];

interface StackRow {
  label: string;
  counts: Record<string, number>;
  shares?: Record<string, number>;
}

interface StackedProps<T extends StackRow> {
  rows: T[];
  series: Series[];
  theme: ChartTheme;
  totalKey?: string;
  labelFormatter?: (label: string | number) => React.ReactNode;
  valueFormatter?: (value: number, name: string, entry: TooltipPayload) => React.ReactNode;
  percent?: boolean;
}

/** Horizontal stacked bars, one row per category; series only appear when they hold someone. */
function StackedBars<T extends StackRow>(props: Readonly<StackedProps<T>>): React.ReactElement {
  const { rows, series, theme, totalKey, labelFormatter, valueFormatter, percent } = props;
  const valueOf = (row: StackRow, key: string): number => (percent ? row.shares?.[key] : row.counts[key]) ?? 0;
  const used = series.filter((s) => rows.some((row) => valueOf(row, s.key) > 0));
  return (
    <ResponsiveContainer width="100%" height={barsHeight(rows.length, true)}>
      <BarChart data={rows} layout="vertical" margin={{ top: 4, right: totalKey ? 140 : 24, bottom: 4, left: 4 }}>
        <CartesianGrid horizontal={false} stroke={theme.grid} />
        <XAxis
          type="number"
          allowDecimals={false}
          domain={percent ? [0, 1] : [0, "auto"]}
          ticks={percent ? SHARE_TICKS : undefined}
          tickFormatter={percent ? (v: number) => `${Math.round(v * 100)}%` : undefined}
          tick={{ ...TICK, fill: theme.ink }}
          stroke={theme.grid}
        />
        <YAxis type="category" dataKey="label" width={AXIS_WIDTH} tick={{ ...TICK, fill: theme.ink }} stroke={theme.grid} />
        <Tooltip
          {...tooltipStyles(theme)}
          labelFormatter={labelFormatter}
          formatter={(value, name, entry) => (valueFormatter ? valueFormatter(Number(value), name, entry) : value)}
        />
        <Legend wrapperStyle={{ color: theme.ink, fontSize: 12 }} />
        {used.map((s, index) => (
          <Bar
            key={s.key}
            name={s.key}
            dataKey={(row: StackRow) => valueOf(row, s.key)}
            stackId="stack"
            fill={s.colour}
            stroke={theme.surface}
            strokeWidth={1}
            barSize={18}
            isAnimationActive={false}
          >
            {totalKey && index === used.length - 1 && <LabelList dataKey={totalKey} content={endLabel(theme)} />}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

const statusText = (status: string): string => status.split("_").join(" ");

export const CampaignCohortChart: React.FC<{ rows: CampaignRow[]; series: Series[]; theme: ChartTheme }> = ({
  rows, series, theme,
}) => {
  const byLabel = new Map(rows.map((row) => [row.label, row]));
  const header = (label: string | number) => {
    const row = byLabel.get(String(label));
    return row ? `${row.campaign} (${statusText(row.status)})` : label;
  };
  return <StackedBars rows={rows} series={series} theme={theme} totalKey="total" labelFormatter={header} />;
};

export const PanelSplitChart: React.FC<{ rows: PanelSplitRow[]; series: Series[]; theme: ChartTheme }> = ({
  rows, series, theme,
}) => <StackedBars rows={rows} series={series} theme={theme} totalKey="note" />;

const armMixValue = (_: number, name: string, entry: TooltipPayload): string => {
  const count = countOf(entry, name);
  return `${count} (${share(count, Number(entry.payload?.members ?? 0))})`;
};

export const ArmMixChart: React.FC<{ rows: ArmMixRow[]; series: Series[]; theme: ChartTheme }> = ({
  rows, series, theme,
}) => <StackedBars rows={rows} series={series} theme={theme} valueFormatter={armMixValue} percent />;

export const FunnelChart: React.FC<{ rows: FunnelRow[]; theme: ChartTheme }> = ({ rows, theme }) => (
  <ResponsiveContainer width="100%" height={barsHeight(rows.length, false)}>
    <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 96, bottom: 4, left: 4 }}>
      <CartesianGrid horizontal={false} stroke={theme.grid} />
      <XAxis type="number" allowDecimals={false} tick={{ ...TICK, fill: theme.ink }} stroke={theme.grid} />
      <YAxis type="category" dataKey="label" width={AXIS_WIDTH} tick={{ ...TICK, fill: theme.ink }} stroke={theme.grid} />
      <Tooltip {...tooltipStyles(theme)} />
      <Bar
        dataKey="value"
        name="Customers"
        fill={SINGLE_SERIES[theme.mode]}
        radius={[0, 4, 4, 0]}
        barSize={18}
        isAnimationActive={false}
      >
        <LabelList dataKey="note" content={endLabel(theme)} />
      </Bar>
    </BarChart>
  </ResponsiveContainer>
);

interface DayTooltipProps {
  active?: boolean;
  payload?: TooltipPayload[];
  theme: ChartTheme;
}

const DayTooltip: React.FC<DayTooltipProps> = ({ active, payload, theme }) => {
  const day: TimelineRow | undefined = payload?.[0]?.payload;
  if (!active || !day) return null;
  const styles = tooltipStyles(theme);
  return (
    <Box p={1} style={styles.contentStyle}>
      <Typography variant="body2" style={styles.labelStyle}>{day.label} (UTC)</Typography>
      <Typography variant="body2">Placed in a cohort: {day.placed}</Typography>
      <Typography variant="body2">Moved between cohorts: {day.moved}</Typography>
      <Typography variant="body2">Left a cohort: {day.exited}</Typography>
    </Box>
  );
};

export const PlacementsChart: React.FC<{ rows: TimelineRow[]; theme: ChartTheme }> = ({ rows, theme }) => (
  <ResponsiveContainer width="100%" height={240}>
    <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
      <CartesianGrid vertical={false} stroke={theme.grid} />
      <XAxis dataKey="label" tick={{ ...TICK, fill: theme.ink }} stroke={theme.grid} minTickGap={12} />
      <YAxis allowDecimals={false} tick={{ ...TICK, fill: theme.ink }} stroke={theme.grid} width={40} />
      <Tooltip cursor={{ fill: theme.hover }} content={<DayTooltip theme={theme} />} />
      <Bar dataKey="placed" name="Placed" fill={SINGLE_SERIES[theme.mode]} radius={[4, 4, 0, 0]} isAnimationActive={false} />
    </BarChart>
  </ResponsiveContainer>
);
